import { SearchBlock } from '@design-system/search-block';
import { Tooltip } from '@design-system/tooltip';
import classNames from 'classnames';
import React, { FC, useRef } from 'react';

import ExtendedGlobalSubmenu from '~/components/ExtendedHeaderSubmenu/ExtendedHeaderSubmenu';
import { ExtendedNavigationLink } from '~/mappers/header/models';

import { cartIconClassName, profileClassName } from '../../constants/constants';
import { useBrowserScreen } from '../../hooks/useBrowserScreen';
import { useJS } from '../../hooks/useJS';
import { CartIcon } from '../cart-icon';
import { Logo } from '../logo';
import { NavigationItem } from '../navigation-item';
import { Profile } from '../profile';
import { NavigationProps } from './models';

export const Navigation: FC<NavigationProps> = ({
  navigationName,
  navigationData,
  headerSubtitle,
  onClickNavItem,
  className,
  closeIcon,
  openIcon,
  activeUrlPath,
  logo,
  moreBtn,
  handleMobileNavigationItemClick,
  search,
  cartIcon,
  profile,
}) => {
  const listRef = useRef<HTMLUListElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const { isMobileView, isDesktopView } = useBrowserScreen();

  const [isJS] = useJS();

  const itemsDataMapper = (navData: ExtendedNavigationLink[]) =>
    navData.map((item) => ({
      label: item.title,
      href: item?.href || '',
    }));

  const [activeKey, setActiveKey] = React.useState<string | null>(null);
  const closeTimer = React.useRef<number | null>(null);

  const openBy = (key: string) => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setActiveKey(key);
  };
  const softClose = (ms = 100) => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setActiveKey(null), ms);
  };

  return (
    <nav
      ref={navRef}
      className={classNames('cds-global-header__navigation', className)}
      aria-label={navigationName}
    >
      <ul ref={listRef} className="cds-global-header__navigation-list">
        {logo && (
          <li className="cds-global-header__navigation-logo">
            <Logo
              className="cds-global-header__desktop-logo"
              image={logo.imageLarge}
              link={logo.link}
            />
            {headerSubtitle && (
              <span className="cds-global-header__navigation-subtitle">
                {headerSubtitle}
              </span>
            )}
          </li>
        )}
        <div
          className={classNames({
            'cds-global-header__navigation-items': isDesktopView,
          })}
        >
          {itemsDataMapper(navigationData).map((item) => (
            <NavigationItem
              navigationData={navigationData}
              activeKey={activeKey}
              onHoverOpen={() => openBy(item.label)}
              onHoverSoftClose={() => softClose(250)}
              key={item.label}
              activeUrlPath={activeUrlPath}
              isMobileView={!isDesktopView}
              openIcon={openIcon}
              closeIcon={closeIcon}
              expandTitle={moreBtn?.title ?? ''}
              isJS={isJS}
              onClickNavItem={onClickNavItem}
              handleMobileNavigationItemClick={handleMobileNavigationItemClick}
              {...item}
            />
          ))}
        </div>
        <div className="cds-global-header__navigation-items--header-right">
          {search && isDesktopView ? (
            <li
              className="cds-global-header__navigation-search
                        cds-global-header__navigation-icon"
            >
              <SearchBlock
                {...search}
                handleBurgerMenu={handleMobileNavigationItemClick}
              />
            </li>
          ) : null}
          {cartIcon && isDesktopView ? (
            <li
              className={classNames(
                cartIconClassName,
                'cds-global-header__navigation-icon'
              )}
            >
              <CartIcon {...cartIcon} />
            </li>
          ) : null}
          {profile && isDesktopView ? (
            <li
              className={classNames(profileClassName, {
                'cds-global-header__navigation-icon': isDesktopView,
              })}
            >
              {isDesktopView ? (
                <Tooltip
                  tooltipWindowClassName="cds-global-header-profile-window"
                  portalQuerySelector=".cds-tooltip"
                  label={profile.headline}
                  isTooltipContentInteractive
                  icon="profile"
                  content={<Profile {...profile} />}
                />
              ) : (
                <Profile {...profile} isMobileView={isMobileView} />
              )}
            </li>
          ) : null}
        </div>
      </ul>
      {isDesktopView && (
        <div
          className={classNames('cds-mega', {
            'cds-mega--open': Boolean(activeKey),
          })}
          data-active-key={activeKey ?? ''}
          onMouseEnter={() => openBy(activeKey ?? '')}
          onMouseLeave={() => softClose(250)}
          role="region"
          aria-label={activeKey ?? undefined}
        >
          <ExtendedGlobalSubmenu
            navigation={navigationData}
            activeKey={activeKey || ''}
          />
        </div>
      )}
    </nav>
  );
};
